import { useQuery } from '@tanstack/react-query';

import { SetStatus, UserRole } from '@/Enums/enum';
import { useCurrentUser } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import type { SubmissionWithCourse } from '@/lib/types';

const schoolId = import.meta.env.VITE_SCHOOL_ID;

/** Lower rank sorts first: waiting → rejected → fully approved → other. */
function submissionStatusRank(submission: {
	admin_approval: string;
	dept_approval: string;
}): number {
	const { admin_approval: ad, dept_approval: dept } = submission;
	const anyWaiting = ad === SetStatus.WAITING || dept === SetStatus.WAITING;
	const anyRejected = ad === SetStatus.REJECTED || dept === SetStatus.REJECTED;
	const bothApproved = ad === SetStatus.APPROVED && dept === SetStatus.APPROVED;
	if (anyWaiting) return 0;
	if (anyRejected) return 1;
	if (bothApproved) return 2;
	return 3;
}

export function useGetAllSubmissions() {
	const { data: currentUser } = useCurrentUser();

	return useQuery<SubmissionWithCourse[]>({
		queryKey: ['get-all-subs', schoolId, currentUser?.user_id, currentUser?.user_role],
		queryFn: async () => {
			if (!currentUser) return [] as SubmissionWithCourse[];

			const userRole = currentUser.user_role as UserRole | undefined;
			const userId = currentUser.user_id;

			// Base query for submissions - fetch all for the school
			// We'll filter by role in JavaScript since Supabase doesn't easily support
			// complex nested relationship filtering
			const { data, error } = await supabase
				.from('submissions')
				.select(
					`
					submission_id,
					admin_approval,
					dept_approval,
					course_id,
					course:courses!submissions_course_id_fkey (
						course_name,
						course_id,
						courses_users (
							user_id,
							course_id,
							user:users (
								user_id,
								user_first,
								user_last,
								user_firebase_id,
								user_dept_chair
							)
						)
					)
				`
				)
				.eq('school_id', schoolId);

			if (error) throw error;

			if (!data) return [];

			// Transform the data and apply role-based filtering
			const transformedData = data
				.map((submission: any) => {
					// Transform courses_users to include dept_chair object structure
					const course = submission.course
						? {
								...submission.course,
								courses_users: submission.course.courses_users?.map((cu: any) => ({
									...cu,
									user: cu.user
										? {
												...cu.user,
												// Transform user_dept_chair string to dept_chair object
												dept_chair: cu.user.user_dept_chair
													? {
															user_id: cu.user.user_dept_chair,
															user_firebase_id: null,
														}
													: null,
											}
										: null,
								})),
							}
						: null;

					return {
						submission_id: submission.submission_id,
						admin_approval: submission.admin_approval,
						dept_approval: submission.dept_approval,
						course_id: submission.course_id,
						school_id: schoolId,
						course,
					};
				})
				.filter((submission: any) => {
					// Apply role-based filtering
					if (userRole === UserRole.ADMIN) {
						// Admin: show all submissions
						return true;
					}

					if (!submission.course?.courses_users) return false;

					const courseUsers = submission.course.courses_users;

					if (userRole === UserRole.DEPT_CHAIR) {
						// Dept chair: submissions for courses where:
						// 1. They are assigned to the course, OR
						// 2. They are the dept chair of someone assigned to the course
						const isAssigned = courseUsers.some(
							(cu: any) => cu.user_id === userId || cu.user?.user_id === userId
						);
						const isDeptChairOfAssignee = courseUsers.some(
							(cu: any) => cu.user?.dept_chair?.user_id === userId
						);
						return isAssigned || isDeptChairOfAssignee;
					}

					// Regular user: only submissions for courses they're assigned to
					return courseUsers.some(
						(cu: any) => cu.user_id === userId || cu.user?.user_id === userId
					);
				})
				.sort((a: any, b: any) => {
					const rankA = submissionStatusRank(a);
					const rankB = submissionStatusRank(b);
					if (rankA !== rankB) return rankA - rankB;
					const nameA = a.course?.course_name ?? '';
					const nameB = b.course?.course_name ?? '';
					return nameA.localeCompare(nameB);
				});

			return transformedData as unknown as SubmissionWithCourse[];
		},
		staleTime: Infinity,
		refetchOnMount: 'always',
		enabled: !!currentUser,
	});
}
